import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  PhoneIcon,
  ArrowSquareOutIcon,
  InfoIcon,
  FileTextIcon,
  HandshakeIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import { useUser } from '../../../../../utils/UserContext';
import {
  CustomerWithDriverOnboarding,
  ModalResourceItem,
  ResourceItem,
} from '../../../../../types/apiTypes';
import { useResources } from '../../../../../hooks/useResources';
import ResourceDetailModal from './ResourceDetailModal';
import PMChecklistModal from './PMChecklistModal';
import RentalRequestCard from './RentalRequestCard';
import FindMeFreightCard from './FindMeFreightCard';

function PMChecklistCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-4 rounded-xl border border-bordergray bg-white p-4 text-left transition-shadow hover:shadow-md"
    >
      <div className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-lg bg-bgyellow">
        <FileTextIcon size={28} weight="regular" className="text-accent" />
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <h3 className="text-base font-semibold text-gray-900">
          PM Inspection Checklist
        </h3>
        <p className="text-sm text-gray-600">
          Printable preventive maintenance checklist for your trucks.
        </p>
      </div>
      <ArrowSquareOutIcon size={18} className="shrink-0 text-gray-400" />
    </button>
  );
}

// "Promote My Business" moved here from CustomerHome and the Sidenav
// (2026-07-22, Scott) so the Find Me Freight entry could take those slots. It
// navigates in-app to the unchanged /dashboard/broker-network route — so it is
// a router link, not the external-window open the ResourceCards do — but wears
// the same full-width row shape as PMChecklistCard.
function PromoteMyBusinessCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      data-testid="resources:promoteMyBusiness"
      onClick={onClick}
      className="flex w-full items-center gap-4 rounded-xl border border-bordergray bg-white p-4 text-left transition-shadow hover:shadow-md"
    >
      <div className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-lg bg-blue-50">
        <HandshakeIcon size={28} weight="regular" className="text-blue-600" />
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <h3 className="text-base font-semibold text-gray-900">
          Promote My Business
        </h3>
        <p className="text-sm text-gray-600">
          Let TACH advocate for you to your broker network.
        </p>
      </div>
      <CaretRightIcon size={18} className="shrink-0 text-gray-400" />
    </button>
  );
}

function ResourceCard({
  item,
  onOpenModal,
}: {
  item: ResourceItem;
  onOpenModal: (modalItem: ModalResourceItem) => void;
}) {
  const handleClick = () => {
    if (item.type === 'link') {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    } else if (item.type === 'phone') {
      window.location.href = `tel:${item.phoneNumber}`;
    } else if (item.type === 'modal') {
      onOpenModal(item);
    }
  };

  const ActionIcon =
    item.type === 'phone'
      ? PhoneIcon
      : item.type === 'modal'
        ? InfoIcon
        : ArrowSquareOutIcon;

  return (
    <button
      onClick={handleClick}
      className="flex h-full w-full flex-col items-start gap-3 rounded-xl border border-bordergray bg-white p-4 text-left transition-shadow hover:shadow-md"
    >
      <div className="flex w-full items-center justify-between">
        {item.logoUrl ? (
          <img
            src={item.logoUrl}
            alt={`${item.name} logo`}
            className="h-[100px] w-[100px] rounded-lg object-contain"
          />
        ) : (
          <div className="flex h-[100px] w-[100px] items-center justify-center rounded-lg bg-gray-100 text-2xl font-semibold text-gray-500">
            {item.name[0]?.toUpperCase() ?? '?'}
          </div>
        )}
        <ActionIcon size={18} className="text-gray-400" />
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-gray-900">{item.name}</h3>
        {item.description && (
          <p className="text-sm text-gray-600">{item.description}</p>
        )}
      </div>
    </button>
  );
}

function ResourcesPage() {
  const { userRecord, userId } = useUser();
  const navigate = useNavigate();
  const status = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isCashAdvanceLocked = !(userRecord as CustomerWithDriverOnboarding)
    ?.is_cash_advance_active;
  const [activeModalItem, setActiveModalItem] =
    useState<ModalResourceItem | null>(null);
  const [isPmChecklistOpen, setIsPmChecklistOpen] = useState(false);

  const { data: sections, isLoading, isError } = useResources(userId);

  if (status !== 'active') {
    return <Navigate to="/dashboard/home" replace />;
  }

  return (
    <div className="flex h-full w-full flex-col items-center overflow-y-auto overflow-x-hidden">
      <div className="flex w-full flex-col gap-8 px-4 pb-10 pt-6 sm:w-[95%] md:w-full md:px-4 lg:w-[1148px] lg:max-w-[1148px] lg:px-0">
        {isLoading && (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-ctadarkgreen" />
          </div>
        )}

        {isError && (
          <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
            We couldn&apos;t load your resources. Please try again later.
          </div>
        )}

        {/* None of these is gated on the resources fetch: they are static
            client-side entries, so they must still be reachable if that
            request fails. Rentals leads because it replaced the config's
            Rentals section — every rental now goes through TACH. */}
        <RentalRequestCard isCashAdvanceLocked={isCashAdvanceLocked} />

        <FindMeFreightCard />

        <PromoteMyBusinessCard
          onClick={() => navigate('/dashboard/broker-network')}
        />

        {!isLoading && !isError && (
          <PMChecklistCard onClick={() => setIsPmChecklistOpen(true)} />
        )}

        {!isLoading &&
          !isError &&
          sections &&
          sections.map((section) => (
            <section key={section.id} className="flex flex-col gap-4">
              <h2 className="text-lg font-semibold text-gray-800">
                {section.title}
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {section.items.map((item) => (
                  <ResourceCard
                    key={item.id}
                    item={item}
                    onOpenModal={setActiveModalItem}
                  />
                ))}
              </div>
            </section>
          ))}

        <ResourceDetailModal
          item={activeModalItem}
          onClose={() => setActiveModalItem(null)}
        />
        <PMChecklistModal
          isOpen={isPmChecklistOpen}
          onClose={() => setIsPmChecklistOpen(false)}
        />
      </div>
    </div>
  );
}

export default ResourcesPage;
